import axios from 'axios';
import { API_BASE_URL } from '../config/api';
import {
  parseAdminUiLabels,
  type AdminUiLabelKey,
} from '../constants/adminUiLabels';

/** Feature flags used to gate calendar overlays and nav items. */
export interface BusinessFeaturePermissions {
  allowCalendar: boolean;
  allowSafetyChecks: boolean;
  allowLoanChecks: boolean;
  allowDeliveryChecks: boolean;
  allowHrModule: boolean;
  allowLocationChecks: boolean;
  /** Whether the Sites / Locations module is enabled (default true). */
  allowLocations: boolean;
  useSiteTerminology: boolean;
}

export function parseBusinessFeaturePermissions(data: Record<string, unknown>): BusinessFeaturePermissions {
  return {
    allowCalendar: data.allow_calendar !== 0 && data.allow_calendar !== false,
    allowSafetyChecks: data.allow_safety_checks !== 0 && data.allow_safety_checks !== false,
    allowLoanChecks: data.allow_loan_checks !== 0 && data.allow_loan_checks !== false,
    allowDeliveryChecks: data.allow_delivery_checks !== 0 && data.allow_delivery_checks !== false,
    allowHrModule: data.allow_hr_module === 1 || data.allow_hr_module === true,
    allowLocationChecks: data.allow_location_checks === 1 || data.allow_location_checks === true,
    // Default to true so existing businesses retain location features when the column is absent
    allowLocations: data.allow_locations !== 0 && data.allow_locations !== false,
    useSiteTerminology: data.use_site_terminology === 1 || data.use_site_terminology === true,
  };
}

export interface BusinessCalendarBootstrap {
  permissions: BusinessFeaturePermissions;
  adminUiLabels: Partial<Record<AdminUiLabelKey, string>>;
}

export async function fetchBusinessFeaturePermissions(): Promise<BusinessFeaturePermissions> {
  const { permissions } = await fetchBusinessCalendarBootstrap();
  return permissions;
}

export async function fetchBusinessCalendarBootstrap(): Promise<BusinessCalendarBootstrap> {
  const token = localStorage.getItem('token');
  const businessId = localStorage.getItem('businessId');
  if (!token || !businessId) {
    return {
      permissions: {
        allowCalendar: false,
        allowSafetyChecks: false,
        allowLoanChecks: false,
        allowDeliveryChecks: false,
        allowHrModule: false,
        allowLocationChecks: false,
        allowLocations: true,
        useSiteTerminology: false,
      },
      adminUiLabels: {},
    };
  }
  const response = await axios.get(`${API_BASE_URL}/businesses/${businessId}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  const data = response.data as Record<string, unknown>;
  return {
    permissions: parseBusinessFeaturePermissions(data),
    adminUiLabels: parseAdminUiLabels(data.admin_ui_labels),
  };
}

export type CalendarOverlayKind = 'booking' | 'safety_check' | 'loan_return' | 'outgoing_delivery' | 'shift';

const OVERLAY_DEFS: {
  kind: CalendarOverlayKind;
  label: string;
  color: string;
  requires: keyof BusinessFeaturePermissions;
}[] = [
  { kind: 'booking', label: 'Bookings', color: '#2563eb', requires: 'allowCalendar' },
  { kind: 'safety_check', label: 'Safety checks', color: '#94a3b8', requires: 'allowSafetyChecks' },
  { kind: 'loan_return', label: 'Loan returns', color: '#22c55e', requires: 'allowLoanChecks' },
  { kind: 'outgoing_delivery', label: 'Outgoing deliveries', color: '#0ea5e9', requires: 'allowDeliveryChecks' },
  { kind: 'shift', label: 'HR shifts', color: '#a78bfa', requires: 'allowHrModule' },
];

export function getCalendarOverlayOptions(permissions: BusinessFeaturePermissions) {
  return OVERLAY_DEFS.filter((d) => permissions[d.requires]);
}
